// src/components/TermsGate.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Modal, Tabs, Typography, Button, Space, Spin, Divider, message } from "antd";
import { SafetyCertificateOutlined, FileTextOutlined } from "@ant-design/icons";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";

// ✅ Keep Quill formatting
import "react-quill/dist/quill.snow.css";

const { Title, Text } = Typography;

const toBool = (v) => v === true || v === "true" || v === 1 || v === "1";
const readFlag = (obj, camel, pascal) => {
  if (!obj) return false;
  if (Object.prototype.hasOwnProperty.call(obj, camel)) return toBool(obj[camel]);
  if (Object.prototype.hasOwnProperty.call(obj, pascal)) return toBool(obj[pascal]);
  return false;
};

const stripHtml = (html) => String(html || "").replace(/<[^>]*>/g, "").trim();

// ✅ only treat as “configured” if version exists AND content is not tiny/placeholder
const isDocConfigured = (doc) => {
  if (!doc) return false;
  const version = doc.version ?? doc.Version ?? null;
  const html = doc.contentHtml ?? doc.ContentHtml ?? "";
  const textLen = stripHtml(html).length;
  return Boolean(version) && textLen >= 50; // adjust threshold if your docs are short
};

export default function TermsGate() {
  const { authData, isInitialized } = useAuth();

  const token = authData?.token;
  const ready = useMemo(
    () => Boolean(token) && (isInitialized === undefined ? true : Boolean(isInitialized)),
    [token, isInitialized]
  );

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState(null);

  const refresh = useCallback(async () => {
    if (!ready) {
      setOpen(false);
      setDocs(null);
      return;
    }

    setLoading(true);

    try {
      // 1) Check status first (only authenticated)
      const statusRes = await axiosInstance.get("/legal/status");
      const s = statusRes.data || {};

      const mustTerms = readFlag(s, "mustAcceptTerms", "MustAcceptTerms");
      const mustPrivacy = readFlag(s, "mustAcceptPrivacy", "MustAcceptPrivacy");

      // If no acceptance required → no modal
      if (!mustTerms && !mustPrivacy) {
        setOpen(false);
        setDocs(null);
        return;
      }

      // 2) Only then fetch docs
      const currentRes = await axiosInstance.get("/legal/current");
      const d = currentRes.data || {};
      setDocs(d);

      const termsOk = isDocConfigured(d.terms);
      const privacyOk = isDocConfigured(d.privacy);
      const configured = termsOk && privacyOk;

      // ✅ Your requested behavior: do NOT popup if docs not configured
      if (!configured) {
        setOpen(false);
        // optional info toast
        // message.warning("Legal documents are not yet configured. Please contact an administrator.");
        return;
      }

      setOpen(true);
    } catch (e) {
      console.error("Legal gate refresh failed:", e);
      const msg = e?.response?.data?.message || e?.message || "Failed to verify legal status.";
      setOpen(false);
      setDocs(null);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [ready]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onClose = () => setOpen(false);

  const onAccept = async () => {
    const termsVersion = docs?.terms?.version ?? docs?.terms?.Version;
    const privacyVersion = docs?.privacy?.version ?? docs?.privacy?.Version;

    if (!termsVersion || !privacyVersion) {
      message.error("Legal documents are not configured.");
      return;
    }

    setBusy(true);
    try {
      await axiosInstance.post("/legal/accept", { termsVersion, privacyVersion });
      message.success("Thanks — accepted.");
      setOpen(false);
      await refresh();
    } catch (e) {
      console.error("Legal accept failed:", e);
      message.error(e?.response?.data?.message || "Failed to accept. Please try again.");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  if (!ready) return null;

  const termsHtml = docs?.terms?.contentHtml ?? docs?.terms?.ContentHtml ?? "<p>Not configured.</p>";
  const privacyHtml = docs?.privacy?.contentHtml ?? docs?.privacy?.ContentHtml ?? "<p>Not configured.</p>";

  const tabItems = [
    {
      key: "terms",
      label: (
        <Space size={6}>
          <FileTextOutlined />
          <span>Terms of Use</span>
          {(docs?.terms?.version ?? docs?.terms?.Version) ? (
            <Text type="secondary">v{docs?.terms?.version ?? docs?.terms?.Version}</Text>
          ) : null}
        </Space>
      ),
      children: (
        <div className="legal-pane">
          <div className="legal-doc-head">
            <Title level={5} style={{ margin: 0 }}>
              {docs?.terms?.title ?? docs?.terms?.Title ?? "Terms of Use"}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Please review before accepting.
            </Text>
          </div>

          <div className="ql-snow legal-quill-shell">
            <div className="ql-editor legal-html" dangerouslySetInnerHTML={{ __html: termsHtml }} />
          </div>
        </div>
      ),
    },
    {
      key: "privacy",
      label: (
        <Space size={6}>
          <SafetyCertificateOutlined />
          <span>Privacy Policy</span>
          {(docs?.privacy?.version ?? docs?.privacy?.Version) ? (
            <Text type="secondary">v{docs?.privacy?.version ?? docs?.privacy?.Version}</Text>
          ) : null}
        </Space>
      ),
      children: (
        <div className="legal-pane">
          <div className="legal-doc-head">
            <Title level={5} style={{ margin: 0 }}>
              {docs?.privacy?.title ?? docs?.privacy?.Title ?? "Privacy Policy"}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              How data is collected, used, and protected.
            </Text>
          </div>

          <div className="ql-snow legal-quill-shell">
            <div className="ql-editor legal-html" dangerouslySetInnerHTML={{ __html: privacyHtml }} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={920}
      centered
      destroyOnClose
      maskClosable
      zIndex={20000}
      className="legal-modal"
      title={
        <Space size={10}>
          <SafetyCertificateOutlined />
          <div>
            <div style={{ fontWeight: 650, lineHeight: 1.1 }}>Usage Terms & Privacy</div>
            <div style={{ fontSize: 12, opacity: 0.72 }}>
              Acceptance required to continue using the platform
            </div>
          </div>
        </Space>
      }
      styles={{ body: { padding: 0 } }}
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "28px 0" }}>
          <Spin />
        </div>
      ) : (
        <div className="legal-wrap">
          <Divider style={{ margin: 0 }} />

          <div className="legal-tabs">
            <Tabs defaultActiveKey="terms" items={tabItems} type="card" animated tabBarGutter={8} />
          </div>

          <div className="legal-footer">
            <Text type="secondary" className="legal-footnote">
              By clicking <b>Accept</b>, you agree to the Terms of Use and Privacy Policy.
            </Text>

            <Space>
              <Button onClick={onClose}>Close</Button>
              <Button type="primary" loading={busy} onClick={onAccept}>
                Accept
              </Button>
            </Space>
          </div>

          <style>{`
            .legal-modal .ant-modal-content{ border-radius: 16px; overflow: hidden; }
            .legal-wrap{ display:flex; flex-direction:column; min-height:520px; background:#fff; }
            .legal-tabs{ padding: 14px 18px 0; }

            .legal-pane{
              background:#fbfbfb; border:1px solid rgba(0,0,0,0.06);
              border-radius:12px; padding:14px; max-height: 420px; overflow:auto;
            }
            .legal-doc-head{
              display:flex; align-items:flex-end; justify-content:space-between; gap:12px;
              padding-bottom:10px; margin-bottom:10px; border-bottom:1px solid rgba(0,0,0,0.06);
            }

            .legal-quill-shell{ background: transparent !important; }
            .legal-pane .ql-container.ql-snow, .legal-pane .ql-snow{ border: 0 !important; background: transparent !important; }
            .legal-pane .ql-editor{ padding: 0 !important; white-space: pre-wrap; }

            .legal-footer{
              margin-top:auto; display:flex; justify-content:space-between; gap:12px; align-items:center;
              padding:12px 18px; background: rgba(255,255,255,0.92);
              border-top:1px solid rgba(0,0,0,0.06);
              backdrop-filter: blur(6px);
              position: sticky; bottom:0;
            }
            .legal-footnote{ font-size:12px; max-width:60%; }
            @media (max-width: 700px){
              .legal-footnote{ max-width:100%; }
              .legal-footer{ flex-direction:column; align-items:stretch; }
              .legal-footer .ant-space{ justify-content:flex-end; }
            }
          `}</style>
        </div>
      )}
    </Modal>
  );
}
